import { useMemo, useEffect, useState } from 'react';
import { zSceneManifest } from './manifest';
import { STYLE_PACKS, getGoogleFontsUrl, getStylePack } from './style-packs';
import { SceneProvider } from './scene-context';
import { SceneSectionShell } from './SceneSectionShell';
import { HeroSection } from './sections/HeroSection';
import { OpeningSequence } from './OpeningSequence';
import { useLenisScroll } from './useLenisScroll';
import { captureFrontendException } from '../../../infrastructure/lib/sentry';

const reportedErrors = new WeakSet<object>();

function reportInvalidManifest(error: object): void {
  // Deduplicate per-manifest to avoid spam on re-renders
  if (reportedErrors.has(error)) return;
  reportedErrors.add(error);
  console.warn('[SceneExperienceRenderer] invalid manifest', error);
  captureFrontendException(error).catch(() => {});
}

type Props = {
  manifest: unknown;
  /**
   * Set when this renderer is one of several simultaneous instances inside a
   * non-scrolling container — e.g. a template-picker grid card — rather than
   * the single full-page instance a real invitation view always is.
   *
   * `SceneExperienceRenderer` was built assuming exactly one instance ever
   * renders on a page: `useLenisScroll()` instantiates Lenis with no `wrapper`,
   * so it hijacks the *page's* native scroll globally, and `OpeningSequence`
   * sets `document.body.style.overflow = 'hidden'` on mount. Mounting several
   * instances at once (one per template card) meant several competing global
   * Lenis instances fighting over the same scroll target, plus a body-scroll
   * lock whose cleanup order is undefined across instances — the page's
   * scroll broke entirely and stayed broken after leaving the picker. Preview
   * mode skips both: no Lenis, no OpeningSequence gate (starts pre-opened).
   */
  preview?: boolean;
};

export function SceneExperienceRenderer({ manifest, preview = false }: Props) {
  const parsed = useMemo(() => zSceneManifest.safeParse(manifest), [manifest]);
  const [opened, setOpened] = useState(preview);
  useLenisScroll(preview);

  useEffect(() => {
    if (!parsed.success) return;
    const pack = parsed.data.template;
    const rawUrl = getGoogleFontsUrl(pack);
    // Validate the URL is a safe https:// Google Fonts URL before injecting into the DOM
    let url: string;
    try {
      const fontUrl = new URL(rawUrl);
      if (fontUrl.protocol !== 'https:' || !fontUrl.hostname.endsWith('fonts.googleapis.com')) return;
      url = fontUrl.toString();
    } catch {
      return;
    }
    if (!document.querySelector(`link[href="${url}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      document.head.appendChild(link);
    }
  }, [parsed]);

  useEffect(() => {
    if (preview || !parsed.success) return;

    const openingEnabled = parsed.data.opening?.enabled ?? true;
    const storageKey = parsed.data.opening?.skipStorageKey
      ?? `invitecloud:opened:${parsed.data.invitationId}`;

    if (!openingEnabled) {
      setOpened(true);
      return;
    }

    setOpened(sessionStorage.getItem(storageKey) === '1');
  }, [parsed, preview]);

  if (!parsed.success) {
    reportInvalidManifest(parsed.error);
    return null;
  }

  const data = parsed.data;
  const pack = getStylePack(data.template);
  const sorted = [...data.sections].sort((a, b) => a.order - b.order);
  const openingEnabled = data.opening?.enabled ?? true;

  return (
    <SceneProvider value={{ manifest: data, pack }}>
      <div
        className="relative font-sans"
        style={{ backgroundColor: pack.colors.background, color: pack.colors.text }}
      >
        {!preview && openingEnabled && !opened && <OpeningSequence onComplete={() => setOpened(true)} />}

        {/* Hero section */}
        <HeroSection hero={data.hero} />

        {/* Ceremony sections */}
        {sorted.map(section => (
          <SceneSectionShell key={section.sectionId} section={section} />
        ))}

        {/* Footer with Venue & Official InviteCloud Platform Watermark */}
        <footer
          className="py-20 px-6 text-center border-t border-amber-500/20 bg-gradient-to-b from-transparent via-black/40 to-black/80"
          style={{ color: pack.colors.text, fontFamily: pack.fonts.body }}
        >
          {data.footer.venue && (
            <div className="max-w-md mx-auto mb-10 p-6 rounded-2xl bg-black/60 border border-amber-500/30 backdrop-blur-md shadow-2xl">
              <p className="text-[11px] uppercase tracking-widest text-amber-300/90 mb-2 font-bold" style={{ fontFamily: pack.fonts.body }}>
                ✦ CELEBRATION VENUE ✦
              </p>
              <p className="text-xl font-bold text-white mb-2" style={{ fontFamily: pack.fonts.display }}>
                {data.footer.venue.name}
              </p>
              {data.footer.venue.address && (
                <p className="text-xs text-amber-100/75 leading-relaxed">
                  {data.footer.venue.address}
                </p>
              )}
            </div>
          )}

          {/* Official InviteCloud Platform Watermark Badge */}
          <div className="flex flex-col items-center justify-center gap-3 pt-6 border-t border-amber-400/20 max-w-sm mx-auto">
            <a
              href="https://invitecloud.in"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full bg-black/80 border border-amber-400/40 text-amber-300 hover:text-amber-200 hover:border-amber-300 hover:scale-105 active:scale-95 transition-all shadow-lg backdrop-blur-md cursor-pointer group"
            >
              <svg className="w-4 h-4 text-amber-400 group-hover:rotate-12 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4 0-2.05 1.53-3.76 3.56-3.97l1.07-.11.5-.95C8.08 7.14 9.94 6 12 6c2.62 0 4.88 1.86 5.39 4.43l.3 1.5 1.53.11c1.56.1 2.78 1.41 2.78 2.96 0 1.65-1.35 3-3 3z"/>
              </svg>
              <span className="text-xs font-black tracking-widest uppercase text-amber-300">
                Crafted with InviteCloud
              </span>
            </a>
            <p className="text-[11px] text-white/50 tracking-wider">
              India's Premier AI-Native Digital Invitation Platform · invitecloud.in
            </p>
          </div>
        </footer>
      </div>
    </SceneProvider>
  );
}
